/**
 * Body measurement helpers
 *
 * Everything the measurement screens (web `pages/Measurements/*`, mobile
 * `screens/Measurements/*`) derive from a list of `UserMeasurements` entries
 * and the catalogue: metric labels and units, value and delta formatting, the
 * per-metric series behind the vitals board, and the newest-first timeline.
 * Pure — safe to memoize on `(measurements, catalog, system)`.
 *
 * The frontend performs NO unit conversion — values arrive from the backend
 * already in the user's preferred `measurement_system`. These helpers only pick
 * the correct display label, format the numeric value, and aggregate the raw
 * entry list into per-metric series.
 *
 * Height is the one metric the backend sends as decimal feet for imperial; we
 * format (not convert) that into ft/in for display.
 *
 * The create / edit form's own helpers (the sparse payload, the add-metric
 * picker, ft/in composition) stay in web's `measurements.utils.ts` until the
 * form is ported.
 */

// Configured dayjs — parses date-only `logged` strings in local time, avoiding
// the UTC-midnight off-by-one that `new Date('2026-06-25')` produces.
import { BODY_MEASUREMENT_KEYS } from '../../types/domain/user-measurement.types';
import { dayjs, formatDisplayDate } from '../date';
import { BODY_MEASUREMENTS } from '../select-options';

import type { MeasurementSystem } from '../../types/auth';
import type {
  BodyMeasurementCatalogItem,
  BodyMeasurementCategory,
  BodyMeasurementKey,
  UserMeasurements,
} from '../../types/domain/user-measurement.types';

// =============================================================================
// METRIC KIND GROUPS (from the unit contract — keyed by conversion kind)
// =============================================================================

const MASS_KEYS = new Set<BodyMeasurementKey>([
  'weight',
  'muscle_mass',
  'bone_mass',
  'lean_body_mass',
  'fat_free_mass',
  'skeletal_muscle_mass',
  'body_fat_mass',
]);

const BODY_LENGTH_KEYS = new Set<BodyMeasurementKey>([
  'waist_circumference',
  'hip_circumference',
  'chest_circumference',
  'neck_circumference',
  'thigh_circumference',
  'calf_circumference',
  'upper_arm_circumference',
  'bicep_circumference',
  'forearm_circumference',
  'shoulder_circumference',
  'ankle_circumference',
  'wrist_circumference',
  'vertical_jump',
  'flexibility_sit_and_reach',
]);

/**
 * Fixed units for pass-through metrics (no system variance). The backend
 * catalog stores these as null, so the frontend owns the labels.
 */
const PASSTHROUGH_UNITS: Partial<Record<BodyMeasurementKey, string>> = {
  grip_strength: 'N',
  body_fat_percentage: '%',
  water_percentage: '%',
  visceral_fat: '',
  body_mass_index: '',
  metabolic_age: 'yrs',
  basal_metabolic_rate_bmr: 'kcal',
  daily_calorie_needs: 'kcal',
  resting_heart_rate: 'bpm',
  recovery_heart_rate: 'bpm',
  blood_pressure_systolic: 'mmHg',
  blood_pressure_diastolic: 'mmHg',
  vo2_max: 'ml/kg/min',
  heart_rate_variability_hrv: 'ms',
  plank_hold_time: 's',
  mile_run_time: 's',
  sprint_time: 's',
  push_ups_max: 'reps',
  steps_per_day: 'steps',
  active_minutes: 'min',
  flexibility_score: '',
  balance_score: '',
  sleep_quality_score: '',
  mood_score: '',
  energy_level: '',
  stress_level: '',
};

// =============================================================================
// LABELS
// =============================================================================

const METRIC_LABELS: Partial<Record<BodyMeasurementKey, string>> =
  Object.fromEntries(
    BODY_MEASUREMENTS.map((option) => [option.value, option.label])
  );

export const getMetricLabel = (key: BodyMeasurementKey): string =>
  METRIC_LABELS[key] ?? key;

// =============================================================================
// UNITS (label selection only — never conversion)
// =============================================================================

export function getMetricUnit(
  key: BodyMeasurementKey,
  system: MeasurementSystem
): string {
  if (MASS_KEYS.has(key)) return system === 'metric' ? 'kg' : 'lbs';
  if (key === 'height') return system === 'metric' ? 'cm' : '';
  if (BODY_LENGTH_KEYS.has(key)) return system === 'metric' ? 'cm' : 'in';
  if (key === 'body_temperature') return system === 'metric' ? '°C' : '°F';
  if (key === 'blood_glucose') return system === 'metric' ? 'mmol/L' : 'mg/dL';
  return PASSTHROUGH_UNITS[key] ?? '';
}

/**
 * The unit to print beside a metric's value.
 *
 * The catalogue owns every unit that varies by measurement system and resolves
 * it for the viewer, so it wins whenever it carries one. It returns null for
 * the 26 metrics that do not vary — percentages, bpm, kcal, ms, mmHg — whose
 * labels have no home in the backend at all, and those fall back to the table
 * above.
 *
 * Read it rather than `catalogItem.unit` directly. Reading the field raw drops
 * the unit for every pass-through metric, which is how the board came to print
 * "18 %" while the page it links to printed "18".
 */
export function resolveMetricUnit(
  key: BodyMeasurementKey,
  catalogUnit: string | null | undefined
): string {
  return catalogUnit ?? PASSTHROUGH_UNITS[key] ?? '';
}

// =============================================================================
// VALUE / DELTA FORMATTING
// =============================================================================

/** Decimal feet → { ft, inches } with the 12-inch rollover guard. */
export function feetToFtIn(decimalFeet: number): {
  ft: number;
  inches: number;
} {
  let ft = Math.floor(decimalFeet);
  let inches = Math.round((decimalFeet - ft) * 12);
  if (inches === 12) {
    ft += 1;
    inches = 0;
  }
  return { ft, inches };
}

/** Round to at most one decimal place and trim a trailing `.0`. */
function formatNumber(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/**
 * Format a metric value for display (without the unit suffix). Height in
 * imperial is the one composite case — returned as `5′11″`.
 */
export function formatMetricValue(
  key: BodyMeasurementKey,
  value: number,
  system: MeasurementSystem
): string {
  if (key === 'height' && system === 'imperial') {
    const { ft, inches } = feetToFtIn(value);
    return `${ft}′${inches}″`;
  }
  return formatNumber(value);
}

export type DeltaDirection = 'up' | 'down' | 'flat';

export interface FormattedDelta {
  direction: DeltaDirection;
  /** Magnitude + unit, no sign — the arrow conveys direction. */
  text: string;
}

/**
 * Format the change since the previous entry. Deltas are intentionally neutral
 * (magnitude + direction only); we do not colour them as good/bad because the
 * desirable direction is goal-dependent.
 */
export function formatDelta(
  key: BodyMeasurementKey,
  delta: number,
  system: MeasurementSystem
): FormattedDelta {
  const direction: DeltaDirection =
    Math.abs(delta) < 0.05 ? 'flat' : delta > 0 ? 'up' : 'down';

  const magnitude = Math.abs(delta);

  // Height imperial arrives as decimal feet; express the change in inches.
  if (key === 'height' && system === 'imperial') {
    return { direction, text: `${formatNumber(magnitude * 12)} in` };
  }

  const unit = getMetricUnit(key, system);
  return {
    direction,
    text: unit ? `${formatNumber(magnitude)} ${unit}` : formatNumber(magnitude),
  };
}

/**
 * A change as a person reads it: signed, with its unit — "−0.6 kg", "+1.5 cm".
 * The sign carries the direction (an arrow alone was too small a cue), and a
 * flat change says "no change" rather than a signless 0.
 */
export function formatSignedDelta(
  key: BodyMeasurementKey,
  delta: number,
  system: MeasurementSystem
): string {
  const { direction, text } = formatDelta(key, delta, system);
  if (direction === 'flat') return 'no change';
  return `${direction === 'up' ? '+' : '−'}${text}`;
}

// =============================================================================
// DATES
// =============================================================================

export function formatLoggedDate(iso: string): string {
  return formatDisplayDate(iso, 'dateShort') ?? '';
}

/** Day-of-month + short weekday, for the timeline's date column. */
export function formatDayParts(iso: string): { day: string; weekday: string } {
  return {
    day: formatDisplayDate(iso, 'day') ?? '',
    weekday: formatDisplayDate(iso, 'weekdayShort') ?? '',
  };
}

// =============================================================================
// SERIES AGGREGATION
// =============================================================================

export interface MetricPoint {
  logged: string;
  value: number;
}

export interface MetricSeries {
  key: BodyMeasurementKey;
  label: string;
  /** Chronological (oldest → newest). */
  points: MetricPoint[];
  latest: number;
  previous: number | null;
  /** latest − previous, or null when there is only one entry. */
  delta: number | null;
  lastLogged: string;
}

/**
 * Turn the raw measurement entry list into one series per tracked metric.
 * Only metrics with at least one logged value appear — never empty cards.
 */
/**
 * Each metric's reading from before an entry: the latest one logged on an
 * EARLIER day, from any other entry. What an entry's readings are compared
 * with — "−0.6 kg since 12 Sept" — wherever that earlier reading was logged.
 */
export function previousReadingsBefore(
  measurements: UserMeasurements[],
  entry: { id: UserMeasurements['id']; logged: string }
): Map<BodyMeasurementKey, MetricPoint> {
  const day = dayjs(entry.logged);
  const earlier = measurements.filter(
    (other) =>
      String(other.id) !== String(entry.id) &&
      dayjs(other.logged).isBefore(day, 'day')
  );

  const previous = new Map<BodyMeasurementKey, MetricPoint>();
  for (const series of buildMetricSeries(earlier)) {
    previous.set(series.key, {
      logged: series.lastLogged,
      value: series.latest,
    });
  }
  return previous;
}

export function buildMetricSeries(
  measurements: UserMeasurements[]
): MetricSeries[] {
  const sorted = [...measurements].sort(
    (a, b) => dayjs(a.logged).valueOf() - dayjs(b.logged).valueOf()
  );

  const series: MetricSeries[] = [];

  for (const key of BODY_MEASUREMENT_KEYS) {
    const points: MetricPoint[] = [];

    for (const entry of sorted) {
      const raw = entry[key] ?? entry.metrics?.[key];
      if (raw !== null && raw !== undefined) {
        points.push({ logged: entry.logged, value: raw });
      }
    }

    if (points.length === 0) continue;

    const latest = points[points.length - 1]!;
    const previous = points.length > 1 ? points[points.length - 2]! : null;

    series.push({
      key,
      label: getMetricLabel(key),
      points,
      latest: latest.value,
      previous: previous ? previous.value : null,
      delta: previous ? latest.value - previous.value : null,
      lastLogged: latest.logged,
    });
  }

  return series;
}

// =============================================================================
// CATEGORIES (the catalogue's families — see groupSeriesByCategory)
// =============================================================================

/**
 * Category order for the board (and the form's add-metric picker).
 *
 * The backend owns the vocabulary; this only fixes the order they read in,
 * which the catalogue itself cannot supply because it is sorted by metric name.
 * Composition leads because it is what most people open the page for.
 *
 * `other` is last and, in practice, never appears: it is the column default and
 * no seeded row carries it. Group off what the catalogue RETURNS rather than
 * off this list, or the board grows a permanently empty section.
 */
export const CATEGORY_ORDER: BodyMeasurementCategory[] = [
  'basic_body_composition',
  'advanced_body_composition',
  'circumference_measurements',
  'cardiovascular_health',
  'performance_strength',
  'activity_lifestyle',
  'health_adjacent',
  'other',
];

export interface MetricCategoryGroup {
  id: BodyMeasurementCategory;
  label: string;
  metrics: MetricSeries[];
}

/**
 * Bucket the tracked series into the catalogue's families.
 *
 * Replaces five hand-rolled section names that had no relationship to the
 * catalogue's categories — the frontend had invented its own taxonomy for the
 * same data, and the two had quietly drifted apart.
 *
 * Metrics the catalogue does not know are dropped rather than bucketed into a
 * fallback: without a catalogue row there is no name, unit or bounds to render,
 * and the metric page would 404 on the card's own link.
 */
export function groupSeriesByCategory(
  series: MetricSeries[],
  catalog: BodyMeasurementCatalogItem[]
): MetricCategoryGroup[] {
  const bySlug = new Map(catalog.map((item) => [item.slug, item]));
  const buckets = new Map<BodyMeasurementCategory, MetricSeries[]>();
  const labels = new Map<BodyMeasurementCategory, string>();

  for (const metric of series) {
    const item = bySlug.get(metric.key);
    if (!item) continue;

    labels.set(item.category, item.category_label);
    const bucket = buckets.get(item.category);
    if (bucket) bucket.push(metric);
    else buckets.set(item.category, [metric]);
  }

  return CATEGORY_ORDER.filter((id) => buckets.has(id)).map((id) => ({
    id,
    label: labels.get(id) ?? id,
    metrics: buckets.get(id)!,
  }));
}

/**
 * The label as shown inside a category. Circumference names drop the redundant
 * "Circumference" suffix, since the section header already says it.
 */
export function categoryScopedLabel(
  label: string,
  category: BodyMeasurementCategory
): string {
  return category === 'circumference_measurements'
    ? label.replace(/\s*Circumference$/, '')
    : label;
}

/** Newest-first list of entries, each reduced to its recorded metrics. */
export interface TimelineEntry {
  id: UserMeasurements['id'];
  logged: string;
  metrics: { key: BodyMeasurementKey; value: number }[];
  /** Photos the viewer may see on the entry; 0 when none (or hidden). */
  imagesCount: number;
}

export function buildTimeline(
  measurements: UserMeasurements[]
): TimelineEntry[] {
  return [...measurements]
    .sort((a, b) => dayjs(b.logged).valueOf() - dayjs(a.logged).valueOf())
    .map((entry) => {
      const metrics = BODY_MEASUREMENT_KEYS.reduce<
        { key: BodyMeasurementKey; value: number }[]
      >((acc, key) => {
        const raw = entry[key] ?? entry.metrics?.[key];
        if (raw !== null && raw !== undefined) acc.push({ key, value: raw });
        return acc;
      }, []);
      return {
        id: entry.id,
        logged: entry.logged,
        metrics,
        imagesCount: entry.images_count ?? 0,
      };
    });
}

export interface TimelineMonthGroup {
  /** `${year}-${monthIndex}` key, or 'unknown' for unparseable dates. */
  key: string;
  /** e.g. "June 2026". */
  label: string;
  entries: TimelineEntry[];
}

/**
 * Group newest-first timeline entries by calendar month. Months and the entries
 * within them keep their newest-first order; the month label carries the year.
 */
export function groupTimelineByMonth(
  entries: TimelineEntry[]
): TimelineMonthGroup[] {
  const groups: TimelineMonthGroup[] = [];
  const byKey = new Map<string, TimelineMonthGroup>();

  for (const entry of entries) {
    const date = dayjs(entry.logged);
    const valid = date.isValid();
    const key = valid ? date.format('YYYY-M') : 'unknown';

    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        label: formatDisplayDate(date, 'monthYearLong') ?? 'Undated',
        entries: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.entries.push(entry);
  }

  return groups;
}
